/**
 * FacebookPageSelector — explizite Auswahl der Facebook-Seite (Rebind).
 *
 * Hintergrund: Ein Facebook-Konto verwaltet oft MEHRERE Seiten. Früher band das
 * Backend stur die erste Seite — dadurch hing das Posten an der falschen Seite.
 * Dieses Panel zeigt nach dem Facebook-Connect ALLE verfügbaren Seiten und lässt
 * den Nutzer bewusst eine wählen und bestätigen.
 *
 * Produktregeln (sichtbar umgesetzt):
 *   • NIE Auto-Erstwahl bei mehreren Seiten — nichts ist vorausgewählt.
 *   • Auch bei genau EINER Seite wird sie explizit angezeigt und muss bestätigt werden.
 *   • Instagram-Status ehrlich: verknüpftes Konto ODER „kein Instagram verknüpft".
 *   • Die bisherige Bindung bleibt bis zur Bestätigung erhalten und ist danach
 *     wiederherstellbar (Rollback).
 *   • Bei ungültigem/abgelaufenem User-Token: klarer „Facebook neu verbinden"-Hinweis
 *     statt leerer Liste — der lang bestehende Kanal könnte unter einem anderen Login liegen.
 *
 * Wird als Inline-Panel (analog ConnectWizardPanel) in der Kanäle-Seite gerendert.
 * Kein Klartext-Token wird je angezeigt — hier fließen nur Metadaten.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Facebook, Instagram, CheckCircle, AlertTriangle, Loader2,
  XCircle, RefreshCw, Users, Undo2, Info,
} from "lucide-react";
import {
  fetchFacebookPages, selectFacebookPage, rollbackFacebookPage,
} from "../../services/socialOAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";

// Followerzahl robust ableiten (Graph liefert mal followers_count, mal fan_count)
const followerText = (page, t) => {
  const n = page?.followers_count ?? page?.fan_count;
  if (n === null || n === undefined) return t("Followerzahl unbekannt", "Follower count unknown");
  const num = Number(n);
  if (Number.isNaN(num)) return t("Followerzahl unbekannt", "Follower count unknown");
  return t(`${num.toLocaleString("de-DE")} Follower`, `${num.toLocaleString("de-DE")} followers`);
};

// Eine einzelne Seiten-Zeile mit Radio-Auswahl
const PageRow = ({ page, isCurrent, isSelected, onSelect }) => {
  const { t } = useLanguage();
  const ig = page?.instagram || null;
  const canPost = page?.can_post !== false; // undefined → optimistisch erlauben
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
        isSelected
          ? "border-fox-fire bg-orange-50"
          : "border-stone-200 bg-white hover:bg-stone-50"
      }`}
    >
      <input
        type="radio"
        name="fb-page"
        className="mt-1 accent-fox-fire flex-shrink-0"
        checked={isSelected}
        onChange={() => onSelect(page.id)}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-stone-800 text-sm break-words">
            {page?.name || t("Unbenannte Seite", "Unnamed page")}
          </span>
          {isCurrent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
              <CheckCircle size={10} /> {t("Aktuell gebunden", "Currently linked")}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5 text-xs text-stone-500 mt-1">
          <Users size={12} className="flex-shrink-0" />
          {followerText(page, t)}
        </div>

        {/* Instagram-Status — ehrlich */}
        <div className="flex items-center gap-1.5 text-xs mt-1">
          <Instagram size={12} className={`flex-shrink-0 ${ig ? "text-pink-500" : "text-stone-300"}`} />
          {ig ? (
            <span className="text-stone-600">
              {t("Instagram:", "Instagram:")} <span className="font-medium">@{ig.username || ig.id}</span>
              {ig.followers_count !== null && ig.followers_count !== undefined && (
                <span className="text-stone-400"> · {t(`${Number(ig.followers_count).toLocaleString("de-DE")} Follower`, `${Number(ig.followers_count).toLocaleString("de-DE")} followers`)}</span>
              )}
            </span>
          ) : (
            <span className="text-stone-400">{t("kein Instagram verknüpft", "No Instagram linked")}</span>
          )}
        </div>

        {/* Kein Posting-Recht auf dieser Seite */}
        {!canPost && (
          <div className="flex items-start gap-1.5 text-[11px] text-amber-600 mt-1.5">
            <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
            {t("Für diese Seite fehlt die Berechtigung zum Veröffentlichen.", "This page is missing permission to publish.")}
          </div>
        )}
      </div>
    </label>
  );
};

const FacebookPageSelector = ({ open, onClose, onBound, onReconnect }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState([]);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [userTokenValid, setUserTokenValid] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rollbackAvailable, setRollbackAvailable] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const panelRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const res = await fetchFacebookPages();
    if (res.ok) {
      setPages(res.pages);
      setCurrentPageId(res.current_page_id);
      setUserTokenValid(res.user_token_valid);
    } else {
      setLoadError(res.message);
    }
    // Produktregel: NIE vorauswählen — auch nicht die aktuell gebundene Seite.
    setSelectedId(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (open) {
      load();
      setRollbackAvailable(false);
      panelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [open, load]);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    const res = await selectFacebookPage(selectedId);
    setSubmitting(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    // Ehrliche Rückmeldung inkl. Instagram-Status
    const igNote = res.instagram
      ? t(` (Instagram @${res.instagram.username || res.instagram.id} verknüpft)`, ` (Instagram @${res.instagram.username || res.instagram.id} linked)`)
      : t(" (kein Instagram verknüpft)", " (no Instagram linked)");
    toast.success(t(`Seite „${res.page_name}" ist jetzt aktiv${igNote}.`, `Page "${res.page_name}" is now active${igNote}.`), { duration: 6000 });
    setCurrentPageId(res.page_id);
    setRollbackAvailable(!!res.rollback_available);
    onBound?.(res);
    // Liste neu laden, damit „Aktuell gebunden" auf die neue Seite wandert
    load();
  };

  const handleRollback = async () => {
    setRollingBack(true);
    const res = await rollbackFacebookPage();
    setRollingBack(false);
    if (!res.ok) {
      toast.error(res.message);
      return;
    }
    toast.success(t("Vorherige Seitenbindung wiederhergestellt.", "Previous page link restored."));
    setRollbackAvailable(false);
    onBound?.(res);
    load();
  };

  return (
    <div
      ref={panelRef}
      data-testid="facebook-page-selector"
      className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-5 space-y-4 animate-fade-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-600">
            <Facebook size={18} className="text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-800 text-sm">{t("Facebook-Seite auswählen", "Select Facebook page")}</h3>
            <p className="text-[11px] text-blue-700 font-medium">
              {t("Wähle die Seite, auf der Förderfuchs posten soll", "Choose the page Förderfuchs should post to")}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-stone-400 hover:text-stone-600 transition-colors p-1"
          title={t("Schließen", "Close")}
        >
          <XCircle size={18} />
        </button>
      </div>

      {/* Ladezustand */}
      {loading && (
        <div className="flex items-center gap-2 p-4 justify-center text-stone-500">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">{t("Seiten werden geladen …", "Loading pages …")}</span>
        </div>
      )}

      {/* Ladefehler */}
      {!loading && loadError && (
        <div className="flex items-start gap-2 p-3 bg-white border border-red-200 rounded-xl">
          <AlertTriangle size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-red-600">{loadError}</p>
            <button
              onClick={load}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-800"
            >
              <RefreshCw size={12} /> {t("Erneut versuchen", "Try again")}
            </button>
          </div>
        </div>
      )}

      {/* User-Token abgelaufen/fehlend → Reconnect nötig */}
      {!loading && !loadError && !userTokenValid && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 bg-white border border-amber-200 rounded-xl">
            <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-800">
                {t("Verbindung zu Facebook nicht mehr gültig", "Facebook connection no longer valid")}
              </p>
              <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                {t(
                  "Die Facebook-Anmeldung ist abgelaufen oder wurde entzogen. Bitte verbinde Facebook neu, um deine Seiten zu laden. Falls deine langjährige Seite hier nicht auftaucht, liegt sie möglicherweise unter einem anderen Facebook-Login — melde dich dann mit genau diesem Konto an.",
                  "Your Facebook login has expired or been revoked. Please reconnect Facebook to load your pages. If your long-standing page doesn't appear here, it may be under a different Facebook login — try logging in with that exact account instead.",
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => onReconnect?.()}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            <Facebook size={15} /> {t("Facebook neu verbinden", "Reconnect Facebook")}
          </button>
        </div>
      )}

      {/* Keine Seiten vorhanden (Token gültig, aber 0 Seiten) */}
      {!loading && !loadError && userTokenValid && pages.length === 0 && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 bg-white border border-stone-200 rounded-xl">
            <Info size={14} className="text-stone-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-stone-600 leading-relaxed">
              {t(
                "Für dieses Facebook-Konto sind keine verwaltbaren Seiten sichtbar. Prüfe im Facebook-Anmeldedialog, ob du die Seiten-Berechtigungen erteilt hast — oder ob deine Seite unter einem anderen Login liegt.",
                "No manageable pages are visible for this Facebook account. Check in the Facebook login dialog whether you granted the page permissions — or whether your page is under a different login.",
              )}
            </p>
          </div>
          <button
            onClick={() => onReconnect?.()}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
          >
            <RefreshCw size={14} /> {t("Mit anderem Konto neu verbinden", "Reconnect with a different account")}
          </button>
        </div>
      )}

      {/* Seitenliste + Bestätigung */}
      {!loading && !loadError && userTokenValid && pages.length > 0 && (
        <div className="space-y-3">
          {/* Produktregel-Hinweis: alte Bindung bleibt bis zur Bestätigung */}
          <div className="flex items-start gap-2 p-2.5 bg-white/70 border border-blue-200 rounded-xl">
            <Info size={13} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-stone-600 leading-relaxed">
              {pages.length === 1
                ? t("Bitte bestätige die Seite ausdrücklich — erst dann wird sie gebunden.", "Please confirm the page explicitly — only then will it be linked.")
                : t("Wähle bewusst EINE Seite aus. Es ist keine Seite vorausgewählt.", "Deliberately choose ONE page. No page is pre-selected.")}{" "}
              {t(
                "Deine bisherige Bindung bleibt erhalten, bis du bestätigst, und lässt sich danach wiederherstellen.",
                "Your previous link stays intact until you confirm, and can be restored afterward.",
              )}
            </p>
          </div>

          <div className="space-y-2">
            {pages.map((page) => (
              <PageRow
                key={page.id}
                page={page}
                isCurrent={page.id === currentPageId}
                isSelected={page.id === selectedId}
                onSelect={setSelectedId}
              />
            ))}
          </div>

          {/* Aktionen */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div>
              {rollbackAvailable && (
                <button
                  onClick={handleRollback}
                  disabled={rollingBack}
                  className="flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-stone-700 transition-colors disabled:opacity-50"
                >
                  {rollingBack ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                  {t("Auswahl rückgängig", "Undo selection")}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-xs font-semibold border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
              >
                {t("Später", "Later")}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!selectedId || submitting}
                data-testid="facebook-page-confirm"
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-semibold bg-fox-fire text-white hover:bg-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                {selectedId && selectedId === currentPageId
                  ? t("Diese Seite bestätigen", "Confirm this page")
                  : t("Diese Seite verbinden", "Connect this page")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacebookPageSelector;
